import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Event, Router, RouterOutlet, Scroll } from "@angular/router";
import { AccessLevel } from "@front/m19-api-client";
import {
  AccountSelectionService,
  AuthService,
  Hotkeys,
  NotificationService,
  ObfuscationService,
} from "@front/m19-services";
import { IButtonDirective, IIconComponent, IToasterListComponent } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { PageLayoutComponent } from "@m19-board/containers/page-layout/page-layout.component";
import { EnvironmentService } from "@m19-board/services/environment.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { filter } from "rxjs/operators";
import { environment } from "@m19-board/environment";
import { BoardSidebarComponent } from "./board-sidebar/board-sidebar.component";

@UntilDestroy()
@Component({
  templateUrl: "./board-layout.component.html",
  encapsulation: ViewEncapsulation.None,
  imports: [
    BoardSidebarComponent,
    IToasterListComponent,
    PageLayoutComponent,
    RouterOutlet,
    IButtonDirective,
    IIconComponent,
    TranslocoDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoardLayoutComponent {
  private notificationService = inject(NotificationService);
  private hotkeys = inject(Hotkeys);
  private obfuscationService = inject(ObfuscationService);
  private router = inject(Router);
  private readonly accountSelectionService = inject(AccountSelectionService);
  protected readonly environmentService = inject(EnvironmentService);
  private readonly authService = inject(AuthService);

  globalWarning = toSignal(this.notificationService.globalWarning$);
  protected readonly productTour = toSignal(this.authService.isProductTour$, { initialValue: false });
  protected readonly autopilotUrl = environment.autopilotUrl;

  constructor() {
    this.router.events
      .pipe(
        filter((event: Event): event is Scroll => event instanceof Scroll),
        untilDestroyed(this),
      )
      .subscribe((e) => {
        const mainContainer = document.getElementById("main");
        if (mainContainer) {
          mainContainer.scrollTop = 0;
        }
      });

    this.hotkeys
      .addShortcut({ altKey: "KeyD" })
      .pipe(untilDestroyed(this))
      .subscribe(() => this.environmentService.printDebugInfo());

    this.hotkeys
      .addShortcut({ altKey: "KeyO" })
      .pipe(untilDestroyed(this))
      .subscribe(() => this.obfuscationService.toggleObfuscationMode(!this.obfuscationService.obfuscation.value));

    // TODO: we should move this to a guard in the router configuration
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(untilDestroyed(this)).subscribe((am) => {
      if (am.accessLevel === AccessLevel.STATS_ONLY) {
        this.router.navigate(["/dashboard360"]);
      }
    });
  }
}
